import { Injectable, inject } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import {
  of,
  map,
  switchMap,
  catchError,
  withLatestFrom,
  mergeMap,
  filter
} from 'rxjs';
import { RecipeBaseActions, recipeBaseState } from './recipe-base.state';
import { RecipeBaseApi } from './recipe-base.api';
import { Store, select } from '@ngrx/store';
import { zefRemoveProgress } from '@zerops/zef/progress';
import { zefRemoveError } from '@zerops/zef/errors';
import { RECIPE_ICON_POPULATE_PARAMS } from '@zerops/models/recipes';

@Injectable()
export class RecipeBaseEffect {
  #actions$ = inject(Actions);
  #store = inject(Store);
  #api = inject(RecipeBaseApi);

  // Cache TTL in milliseconds (5 minutes default)
  readonly CACHE_TTL = 5 * 60 * 1000;

  /**
   * Generates a cache key for requests based on parameters
   * This ensures different filter combinations are cached separately
   */
  #generateCacheKey(baseKey: string, params: Record<string, any> = {}): string {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {} as Record<string, any>);

    const paramString = Object.keys(sortedParams).length > 0
      ? JSON.stringify(sortedParams)
      : '';

    return paramString ? `${baseKey}:${btoa(paramString)}` : baseKey;
  }

  /**
   * Checks if cache entry is expired based on TTL
   */
  #isCacheExpired(timestamp: number | undefined): boolean {
    if (!timestamp) return true;
    return Date.now() - timestamp > this.CACHE_TTL;
  }

  onLoadARecipe$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadRecipe),
    withLatestFrom(this.#store.pipe(select(recipeBaseState.selectEntities))),
    switchMap(([ action, entities ]) => {
      const { id } = action;

      // Cache hit: recipe already exists
      if (entities.recipes[id]) {
        return of(RecipeBaseActions.loadRecipesCancel({
          key: 'single-recipe',
          cacheKey: id
        }));
      }

      // Cache miss: load recipe
      return of(RecipeBaseActions.loadRecipes({
        params: {
          'filters[slug][$eq]': id
        }
      }));
    })
  ));

  onLoadRecipes$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadRecipes),
    withLatestFrom(
      this.#store.pipe(select(recipeBaseState.selectRecipeLists)),
      this.#store.pipe(select(recipeBaseState.selectTimestamps))
    ),
    switchMap(([ action, recipeLists, timestamps ]) => {
      const baseKey = action.data.key || 'default';
      const params = action.data.params || {};
      const cacheKey = this.#generateCacheKey(baseKey, params);

      // Cache hit: check if we have data AND it's not expired
      const hasData = recipeLists[cacheKey] !== undefined;
      const isExpired = this.#isCacheExpired(timestamps.recipes[cacheKey]);

      if (hasData && !isExpired) {
        return of(RecipeBaseActions.loadRecipesCancel({
          key: baseKey,
          cacheKey
        }));
      }
      // Cache miss: proceed with API call
      return this.#api
        .list$({
          ...params,
          'populate[recipeCategories][populate]': '*',
          'populate[recipeLanguageFrameworks][populate]': '*',
          ...RECIPE_ICON_POPULATE_PARAMS
        })
        .pipe(
          map(({ data }) => RecipeBaseActions.loadRecipesSuccess({
            data,
            key: cacheKey // Use cache key for storage
          }, action)),
          catchError((err) => of(RecipeBaseActions.loadRecipesFail(err, action)))
        );
    })
  ));

  onLoadRecipeCategories$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadRecipeCategories),
    withLatestFrom(
      this.#store.pipe(select(recipeBaseState.selectCategoryLists)),
      this.#store.pipe(select(recipeBaseState.selectTimestamps))
    ),
    switchMap(([ action, categoryLists, timestamps ]) => {
      const baseKey = action.data.key || 'default';
      const params = action.data.params || {};
      const cacheKey = this.#generateCacheKey(baseKey, params);

      // Cache hit: check if we have data AND it's not expired
      const hasData = categoryLists[cacheKey] !== undefined;
      const isExpired = this.#isCacheExpired(timestamps.categories[cacheKey]);

      if (hasData && !isExpired) {
        return of(RecipeBaseActions.loadRecipeCategoriesCancel({
          key: baseKey,
          cacheKey
        }));
      }

      // Cache miss: proceed with API call
      return this.#api
        .categories$(params)
        .pipe(
          map(({ data }) => RecipeBaseActions.loadRecipeCategoriesSuccess({
            data,
            key: cacheKey // Use cache key for storage
          }, action)),
          catchError((err) => of(RecipeBaseActions.loadRecipeCategoriesFail(err, action)))
        );
    })
  ));

  onLoadRecipeLanguagesFrameworks$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadRecipeLanguageFrameworks),
    withLatestFrom(
      this.#store.pipe(select(recipeBaseState.selectLanguageFrameworkLists)),
      this.#store.pipe(select(recipeBaseState.selectTimestamps))
    ),
    switchMap(([ action, languageFrameworkLists, timestamps ]) => {
      const baseKey = action.data.key || 'default';
      const params = action.data.params || {};
      const cacheKey = this.#generateCacheKey(baseKey, params);

      // Cache hit: check if we have data AND it's not expired
      const hasData = languageFrameworkLists[cacheKey] !== undefined;
      const isExpired = this.#isCacheExpired(timestamps.languageFrameworks[cacheKey]);

      if (hasData && !isExpired) {
        return of(RecipeBaseActions.loadRecipeLanguageFrameworksCancel({
          key: baseKey,
          cacheKey
        }));
      }

      // Cache miss: proceed with API call
      return this.#api
        .languagesFrameworks$(params)
        .pipe(
          map(({ data }) => RecipeBaseActions.loadRecipeLanguageFrameworksSuccess({
            data,
            key: cacheKey // Use cache key for storage
          }, action)),
          catchError((err) => of(RecipeBaseActions.loadRecipeLanguageFrameworksFail(err, action)))
        );
    })
  ));

  onLoadGitHubRecipe$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadGithubRecipe),
    withLatestFrom(this.#store.pipe(select(recipeBaseState.selectEntities))),
    switchMap(([ action, entities ]) => {
      const { url, specificYaml } = action.data;
      const recipeId = btoa(url);

      // Cache hit: recipe already exists
      if (entities.githubRecipes[recipeId]) {
        return of(RecipeBaseActions.loadGithubRecipeCancel({
          url,
          recipeId
        }));
      }

      // Cache miss: proceed with API call
      return this.#api.loadGitHubRecipe(url, specificYaml).pipe(
        map((data) => RecipeBaseActions.loadGithubRecipeSuccess({ data }, action)),
        catchError((err) => of(RecipeBaseActions.loadGithubRecipeFail(err, action)))
      );
    })
  ));

  // Cancel effects that handle progress and error cleanup for cache hits
  onLoadRecipesCancel$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadRecipesCancel),
    mergeMap(() => [
      zefRemoveProgress(RecipeBaseActions.loadRecipes.type),
      zefRemoveError(RecipeBaseActions.loadRecipes.type)
    ])
  ));

  onLoadRecipeCategoriesCancel$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadRecipeCategoriesCancel),
    mergeMap(() => [
      zefRemoveProgress(RecipeBaseActions.loadRecipeCategories.type),
      zefRemoveError(RecipeBaseActions.loadRecipeCategories.type)
    ])
  ));

  onLoadRecipeLanguageFrameworksCancel$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadRecipeLanguageFrameworksCancel),
    mergeMap(() => [
      zefRemoveProgress(RecipeBaseActions.loadRecipeLanguageFrameworks.type),
      zefRemoveError(RecipeBaseActions.loadRecipeLanguageFrameworks.type)
    ])
  ));

  onLoadGitHubRecipeCancel$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadGithubRecipeCancel),
    mergeMap(() => [
      zefRemoveProgress(RecipeBaseActions.loadGithubRecipe.type),
      zefRemoveError(RecipeBaseActions.loadGithubRecipe.type)
    ])
  ));

  // Related recipes effects
  onLoadRelatedRecipes$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadRelatedRecipes),
    withLatestFrom(this.#store.pipe(select(recipeBaseState.selectEntities))),
    switchMap(([ action, entities ]) => {
      const { recipeId, limit = 4 } = action;

      // Cache hit: already have related recipes for this ID
      if (entities.relatedRecipes[recipeId]?.length > 0) {
        return of(RecipeBaseActions.loadRelatedRecipesCancel({ recipeId }));
      }

      return this.#api.relatedRecipes$(recipeId, limit).pipe(
        map(({ data }) => RecipeBaseActions.loadRelatedRecipesSuccess({
          recipeId,
          data
        })),
        catchError((err) => of(RecipeBaseActions.loadRelatedRecipesFail(err, action)))
      );
    })
  ));

  onLoadRelatedRecipesCancel$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadRelatedRecipesCancel),
    mergeMap(() => [
      zefRemoveProgress(RecipeBaseActions.loadRelatedRecipes.type),
      zefRemoveError(RecipeBaseActions.loadRelatedRecipes.type)
    ])
  ));

  // Static content effects (environment guides, service type docs)
  onLoadStaticContent$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadStaticContent),
    withLatestFrom(this.#store.pipe(select(recipeBaseState.selectStaticContentLoaded))),
    filter(([ , loaded ]) => !loaded),
    switchMap(() =>
      this.#api.staticContent$().pipe(
        map((data) => RecipeBaseActions.loadStaticContentSuccess({ data })),
        catchError((err) => of(RecipeBaseActions.loadStaticContentFail(err, RecipeBaseActions.loadStaticContent())))
      )
    )
  ));

  // Auto-load static content when a recipe is loaded — native OR GitHub. The
  // service-type docs / environment guides drive the KB quick-links + (native)
  // env guides; a GitHub recipe needs them too for the service-type KB section.
  onLoadStaticContentOnRecipeLoad$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadRecipe, RecipeBaseActions.loadGithubRecipe),
    map(() => RecipeBaseActions.loadStaticContent())
  ));

  onLoadStaticContentCancel$ = createEffect(() => this.#actions$.pipe(
    ofType(RecipeBaseActions.loadStaticContentCancel),
    mergeMap(() => [
      zefRemoveProgress(RecipeBaseActions.loadStaticContent.type),
      zefRemoveError(RecipeBaseActions.loadStaticContent.type)
    ])
  ));
}
